import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Typography, Row, Col, Button, Spin, message, Statistic, Space, Tooltip, Select, Popconfirm, Modal, Form, Input, InputNumber, Checkbox, Alert, List, Divider, DatePicker, AutoComplete } from 'antd';
import { ReloadOutlined, SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, InfoCircleOutlined, CalendarOutlined, UserOutlined, FileTextOutlined, ClockCircleOutlined, PrinterOutlined, DownloadOutlined, EditFilled } from '@ant-design/icons';
import { listUsers, createUser, updateUser, deleteUser, resetDeviceId, toggleSupervisorLeave, listDeletedUsers, restoreUser, getUniquePaymentAccounts, getUniqueJobs, createTemporaryUser, listTemporaryUsers, convertTemporaryToPermanent, deleteTemporaryUser } from '../services/userService';
import { renderActivityDot } from '../utils/activityDot';
import UserForm from '../components/UserForm';
import { themeConfig } from '../config/theme';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Search } = Input;

const UserManagement = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [showDeletedUsers, setShowDeletedUsers] = useState(false);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [deletedUsersLoading, setDeletedUsersLoading] = useState(false);
  const [isPrintModalVisible, setIsPrintModalVisible] = useState(false);
  
  // Bulk Edit State
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isBulkEditModalVisible, setIsBulkEditModalVisible] = useState(false);
  const [bulkEditForm] = Form.useForm();
  const [bulkEditLoading, setBulkEditLoading] = useState(false);
  const [bulkEditFields, setBulkEditFields] = useState([]);
  
  // Payment Account Autofill State
  const [paymentAccountOptions, setPaymentAccountOptions] = useState([]);
  
  // Job Autofill State
  const [jobOptions, setJobOptions] = useState([]);
  
  // Temporary User State
  const [temporaryUsers, setTemporaryUsers] = useState([]);
  const [showTemporaryUsers, setShowTemporaryUsers] = useState(false);
  const [isTemporaryModalVisible, setIsTemporaryModalVisible] = useState(false);
  const [isConvertModalVisible, setIsConvertModalVisible] = useState(false);
  const [tempUserForm] = Form.useForm();
  const [convertForm] = Form.useForm();
  const [tempUserToConvert, setTempUserToConvert] = useState(null);
  
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await listUsers();
      console.log('API Response:', data); // Debug log
      
      // Handle different possible response structures
      let usersArray = [];
      if (Array.isArray(data)) {
        usersArray = data;
      } else if (data && Array.isArray(data.users)) {
        usersArray = data.users;
      } else if (data && Array.isArray(data.data)) {
        usersArray = data.data;
      } else {
        console.warn('Unexpected API response structure:', data);
        usersArray = [];
      }
      
      setUsers(usersArray);
    } catch (error) {
      console.error('Error fetching users:', error);
      messageApi.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedUsers = async () => {
    setDeletedUsersLoading(true);
    try {
      const data = await listDeletedUsers();
      setDeletedUsers(data.deletedUsers || []);
    } catch (error) {
      console.error('Error fetching deleted users:', error);
      messageApi.error('Failed to fetch deleted users');
    } finally {
      setDeletedUsersLoading(false);
    }
  };

  const fetchTemporaryUsers = async () => {
    setLoading(true);
    try {
      const data = await listTemporaryUsers();
      setTemporaryUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching temporary users:', error);
      messageApi.error('Failed to fetch temporary users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showTemporaryUsers) {
      fetchTemporaryUsers();
    } else {
      fetchUsers();
    }
  }, [showTemporaryUsers]);

  // Fetch payment account options for autofill
  useEffect(() => {
    const fetchPaymentAccounts = async () => {
      try {
        const response = await getUniquePaymentAccounts();
        const accounts = response.paymentAccounts || [];
        setPaymentAccountOptions(accounts.map(account => ({ value: account, label: account })));
      } catch (error) {
        console.error('Error fetching payment accounts:', error);
        // Don't show error to user, just log it
      }
    };

    const fetchJobs = async () => {
      try {
        const response = await getUniqueJobs();
        const jobs = response.jobs || [];
        setJobOptions(jobs.map(job => ({ value: job, label: job })));
      } catch (error) {
        console.error('Error fetching jobs:', error);
        // Don't show error to user, just log it
      }
    };

    fetchPaymentAccounts();
    fetchJobs();
  }, []);

  const handleAddUser = () => {
    setEditingUser(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleAddTemporaryUser = () => {
    tempUserForm.resetFields();
    setIsTemporaryModalVisible(true);
  };

  const handleTemporaryModalOk = async () => {
    try {
      const values = await tempUserForm.validateFields();
      await createTemporaryUser({
        MobileNumber: values.mobileNumber,
        FullName: values.fullName,
        Rate: values.rate,
        Designation: values.designation || undefined,
      });
      messageApi.success('Temporary user created successfully');
      setIsTemporaryModalVisible(false);
      tempUserForm.resetFields();
      fetchTemporaryUsers();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create temporary user';
      messageApi.error(errorMessage);
    }
  };

  const handleConvertToPermanent = (tempUser) => {
    setTempUserToConvert(tempUser);
    convertForm.resetFields();
    // Pre-fill form with temp user data
    convertForm.setFieldsValue({
      mobileNumber: tempUser.MobileNumber,
      fullName: tempUser.FullName,
      rate: tempUser.Rate,
    });
    setIsConvertModalVisible(true);
  };

  const handleConvertModalOk = async () => {
    try {
      const values = await convertForm.validateFields();
      await convertTemporaryToPermanent(tempUserToConvert.MobileNumber, {
        mobileNumber: values.mobileNumber,
        FullName: values.fullName,
        Job: values.job,
        UserType: values.userType,
        EmploymentType: values.employmentType,
        Rate: values.rate,
        AadhaarCard: values.aadhaarCard,
        DateOfJoining: values.dateOfJoining ? values.dateOfJoining.format('YYYY-MM-DD') : undefined,
        PaymentAccount: values.paymentAccount,
      });
      messageApi.success('Temporary user converted to permanent successfully');
      setIsConvertModalVisible(false);
      convertForm.resetFields();
      setTempUserToConvert(null);
      fetchTemporaryUsers();
      fetchUsers();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to convert user';
      messageApi.error(errorMessage);
    }
  };

  const handleDeleteTemporaryUser = async (mobileNumber) => {
    try {
      await deleteTemporaryUser(mobileNumber);
      messageApi.success('Temporary user deleted successfully');
      fetchTemporaryUsers();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete temporary user';
      messageApi.error(errorMessage);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    console.log(user);
    
    // Reset form first, then set new values
    form.resetFields();
    
    // Convert DateOfJoining to dayjs format if it exists
    const processedValues = {
      fullName: user.FullName,
      mobileNumber: user.MobileNumber,
      aadhaarCard: user.AadhaarCard,
      rate: user.Rate,
      job: user.Job,
      paymentAccount: user.PaymentAccount,
      employmentType: user.EmploymentType,
      userType: user.UserType,
      dateOfJoining: user.DateOfJoining ? dayjs(user.DateOfJoining) : null,
    };
    
    // Set form values after a small delay to ensure form is ready
    setTimeout(() => {
      form.setFieldsValue(processedValues);
    }, 100);
    
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingUser(null);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      const userData = {
        FullName: values.fullName,
        MobileNumber: values.mobileNumber,
        AadhaarCard: values.aadhaarCard,
        Rate: values.rate || undefined,
        Job: values.job,
        PaymentAccount: values.paymentAccount,
        EmploymentType: values.employmentType,
        UserType: values.userType,
        DateOfJoining: values.dateOfJoining ? values.dateOfJoining.format('YYYY-MM-DD') : undefined,
      };

      if (editingUser) {
        await updateUser(editingUser.MobileNumber, { ...userData });
        messageApi.success('User updated successfully');
      } else {
        await createUser({ ...userData });
        messageApi.success('User created successfully');
      }
      setIsModalVisible(false);
      form.resetFields();
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      messageApi.error('Failed to save user');
    }
  };

  const handleDeleteUser = async (mobileNumber) => {
    try {
      await deleteUser(mobileNumber);
      messageApi.success('User has been soft deleted successfully');
      fetchUsers();
    } catch (error) {
      messageApi.error('Failed to delete user');
    }
  };

  const handleRestoreUser = async (mobileNumber) => {
    try {
      await restoreUser(mobileNumber);
      messageApi.success('User restored successfully');
      fetchDeletedUsers();
      fetchUsers(); // Refresh active users list
    } catch (error) {
      messageApi.error('Failed to restore user');
    }
  };

  const handleResetDeviceId = async (mobileNumber) => {
    try {
      await resetDeviceId(mobileNumber);
      messageApi.success('Device ID reset successfully');
      fetchUsers();
    } catch (error) {
      messageApi.error('Failed to reset device ID');
    }
  };

  const handleToggleSupervisorLeave = async (mobileNumber) => {
    try {
      const result = await toggleSupervisorLeave(mobileNumber);
      messageApi.success(result.message);
      fetchUsers();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to toggle leave status';
      messageApi.error(errorMessage);
    }
  };

  const toggleDeletedUsersView = () => {
    if (!showDeletedUsers) {
      fetchDeletedUsers();
    }
    setShowDeletedUsers(!showDeletedUsers);
  };

  const handlePrint = () => {
    setIsPrintModalVisible(true);
  };

  const handlePrintContent = () => {
    const printContent = document.getElementById('printable-content');
    const originalContents = document.body.innerHTML;
    
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContents;
    
    // Re-render the component after printing
    window.location.reload();
  };

  const handleDownloadCSV = () => {
    const headers = [
      'Full Name',
      'Mobile Number', 
      'Aadhaar Card',
      'Rate',
      'Job',
      'Payment Account',
      'Employment Type',
      'User Type',
      'Date of Joining',
      'Leave Status',
      'Active Status'
    ];

    const csvContent = [
      headers.join(','),
      ...filteredUsers.map(user => [
        `"${user.FullName || ''}"`,
        `"${user.MobileNumber || ''}"`,
        `"${user.AadhaarCard || ''}"`,
        `"${user.Rate || ''}"`,
        `"${user.Job || ''}"`,
        `"${user.PaymentAccount || ''}"`,
        `"${user.EmploymentType || 'SHIFT'}"`,
        `"${user.UserType || ''}"`,
        `"${user.DateOfJoining || ''}"`,
        `"${(user.UserType === 'SUPERVISOR' || user.UserType === 'LEAD') ? (user.IsOnLeave ? 'On Leave' : 'Active') : 'N/A'}"`,
        `"${user.activeStatus || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'users_data.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Bulk Edit Functions
  const handleBulkEdit = () => {
    if (selectedRowKeys.length === 0) {
      messageApi.warning('Please select users to edit');
      return;
    }
    setIsBulkEditModalVisible(true);
    setBulkEditFields([]);
    bulkEditForm.resetFields();
  };

  const handleBulkEditCancel = () => {
    setIsBulkEditModalVisible(false);
    setBulkEditFields([]);
    bulkEditForm.resetFields();
  };

  const handleBulkEditOk = async () => {
    try {
      const values = await bulkEditForm.validateFields();
      setBulkEditLoading(true);

      const selectedUsers = users.filter(user => selectedRowKeys.includes(user.MobileNumber));
      const updatePromises = [];

      for (const user of selectedUsers) {
        const updateData = {};
        
        // Only update fields that were selected and have values
        bulkEditFields.forEach(field => {
          if (values[field] !== undefined && values[field] !== null && values[field] !== '') {
            switch (field) {
              case 'rate':
                updateData.Rate = values.rate;
                break;
              case 'job':
                updateData.Job = values.job;
                break;
              case 'employmentType':
                updateData.EmploymentType = values.employmentType;
                break;
              case 'userType':
                updateData.UserType = values.userType;
                break;
              case 'dateOfJoining':
                updateData.DateOfJoining = values.dateOfJoining.format('YYYY-MM-DD');
                break;
              case 'paymentAccount':
                updateData.PaymentAccount = values.paymentAccount;
                break;
              case 'aadhaarCard':
                updateData.AadhaarCard = values.aadhaarCard;
                break;
              default:
                break;
            }
          }
        });

        if (Object.keys(updateData).length > 0) {
          updatePromises.push(updateUser(user.MobileNumber, updateData));
        }
      }

      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
        messageApi.success(`Successfully updated ${selectedUsers.length} users`);
        setSelectedRowKeys([]);
        fetchUsers();
      } else {
        messageApi.warning('No fields were selected for update');
      }

      setIsBulkEditModalVisible(false);
      setBulkEditFields([]);
      bulkEditForm.resetFields();
    } catch (error) {
      console.error('Error in bulk edit:', error);
      messageApi.error('Failed to update users');
    } finally {
      setBulkEditLoading(false);
    }
  };

  const handleFieldSelectionChange = (checkedValues) => {
    setBulkEditFields(checkedValues);
    bulkEditForm.resetFields();
  };

  const getSelectedUsersInfo = () => {
    const selectedUsers = users.filter(user => selectedRowKeys.includes(user.MobileNumber));
    return {
      count: selectedUsers.length,
      names: selectedUsers.map(user => user.FullName).join(', '),
      summary: {
        workers: selectedUsers.filter(u => u.UserType === 'WORKER').length,
        leads: selectedUsers.filter(u => u.UserType === 'LEAD').length,
        supervisors: selectedUsers.filter(u => u.UserType === 'SUPERVISOR').length,
        fixed: selectedUsers.filter(u => u.EmploymentType === 'FIXED').length,
        shift: selectedUsers.filter(u => u.EmploymentType === 'SHIFT').length,
        withRate: selectedUsers.filter(u => u.Rate).length,
        withoutRate: selectedUsers.filter(u => !u.Rate).length,
      }
    };
  };

  const filteredUsers = users.filter(user =>
    user.FullName.toLowerCase().includes(searchText.toLowerCase()) ||
    user.MobileNumber.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Full Name',
      dataIndex: 'FullName',
      key: 'fullName',
      render: (text, record) => {
        return (
          <span>
            {renderActivityDot(record.activeStatus)}
            {text}
          </span>
        );
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search by name"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => <SearchOutlined style={{ color: filtered ? themeConfig.status.info : undefined }} />,
      onFilter: (value, record) =>
        record.FullName.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Job',
      dataIndex: 'Job',
      key: 'job',
      render: (text) => text || 'N/A',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        // Get unique job titles from current data
        const uniqueJobs = [...new Set(users.map(user => user.Job).filter(job => job && job.trim() !== ''))].sort();
        
        return (
          <div style={{ padding: 8, maxHeight: 300, overflowY: 'auto' }}>
            <div style={{ marginBottom: 8 }}>
              <Checkbox
                indeterminate={selectedKeys.length > 0 && selectedKeys.length < uniqueJobs.length}
                checked={selectedKeys.length === uniqueJobs.length}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedKeys(uniqueJobs);
                  } else {
                    setSelectedKeys([]);
                  }
                }}
              >
                Select All
              </Checkbox>
            </div>
            <div style={{ borderTop: `1px solid ${themeConfig.background.container}`, paddingTop: 8 }}>
              {uniqueJobs.map(job => (
                <div key={job} style={{ marginBottom: 4 }}>
                  <Checkbox
                    checked={selectedKeys.includes(job)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedKeys([...selectedKeys, job]);
                      } else {
                        setSelectedKeys(selectedKeys.filter(key => key !== job));
                      }
                    }}
                  >
                    {job}
                  </Checkbox>
                </div>
              ))}
            </div>
            <div style={{ borderTop: `1px solid ${themeConfig.background.container}`, paddingTop: 8, marginTop: 8 }}>
              <Space>
                <Button
                  type="primary"
                  onClick={() => confirm()}
                  size="small"
                  style={{ width: 90 }}
                >
                  OK
                </Button>
                <Button
                  onClick={() => clearFilters()}
                  size="small"
                  style={{ width: 90 }}
                >
                  Reset
                </Button>
              </Space>
            </div>
          </div>
        );
      },
      filterIcon: filtered => <SearchOutlined style={{ color: filtered ? themeConfig.status.info : undefined }} />,
      onFilter: (value, record) => {
        // For checkbox filters, value is the array of selected keys
        if (!value || value.length === 0) return true;
        return value.includes(record.Job);
      },
    },
    {
      title: 'Mobile Number',
      dataIndex: 'MobileNumber',
      key: 'mobileNumber',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search by mobile"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => <SearchOutlined style={{ color: filtered ? themeConfig.status.info : undefined }} />,
      onFilter: (value, record) =>
        record.MobileNumber.toLowerCase().includes(value.toLowerCase()),
    },

    {
      title: 'Rate',
      dataIndex: 'Rate',
      key: 'rate',
      render: (rate) => rate ? `₹${rate}` : <span style={{ color: themeConfig.status.error }}>Not Set</span>,
      filters: [
        { text: 'Has Rate', value: 'hasRate' },
        { text: 'No Rate', value: 'noRate' },
      ],
      onFilter: (value, record) => {
        if (value === 'hasRate') return record.Rate && record.Rate > 0;
        if (value === 'noRate') return !record.Rate || record.Rate === 0;
        return true;
      },
    },
    {
      title: 'Employment Type',
      dataIndex: 'EmploymentType',
      key: 'employmentType',
      render: (text) => text || '',
      filters: [
        { text: 'Fixed', value: 'FIXED' },
        { text: 'Shift', value: 'SHIFT' },
      ],
      onFilter: (value, record) => record.EmploymentType === value,
    },
    {
      title: 'User Type',
      dataIndex: 'UserType',
      key: 'userType',
      filters: [
        { text: 'Worker', value: 'WORKER' },
        { text: 'Lead', value: 'LEAD' },
        { text: 'Supervisor', value: 'SUPERVISOR' },
      ],
      onFilter: (value, record) => record.UserType === value,
    },
    {
      title: 'Leave Status',
      key: 'leaveStatus',
      render: (text, record) => {
        if (record.IsOnLeave) {
          return <Tag color="red">On Leave</Tag>;
        } else {
          return <Tag color="green">Active</Tag>;
        }
      },
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'On Leave', value: 'onLeave' },
      ],
      onFilter: (value, record) => {
        if (value === 'active') return !record.IsOnLeave;
        if (value === 'onLeave') return record.IsOnLeave;
        return true;
      },
    },

    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Space size="middle">
          <Button type="link" onClick={() => handleEditUser(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => handleDeleteUser(record.MobileNumber)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
          <Button type="link" onClick={() => handleResetDeviceId(record.MobileNumber)}>
            Reset Device ID
          </Button>
          {(record.UserType === 'SUPERVISOR' || record.UserType === 'LEAD' || record.UserType === 'WORKER') && (
            <Button 
              type="link" 
              onClick={() => handleToggleSupervisorLeave(record.MobileNumber)}
              style={{ 
                color: record.IsOnLeave ? themeConfig.status.success : themeConfig.status.warningLight,
                fontWeight: 'bold'
              }}
            >
              {record.IsOnLeave ? 'Activate' : 'Set Leave'}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Input.Search
            placeholder="Search by name or mobile number"
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 300 }}
        />
        <Space>
          <Button onClick={toggleDeletedUsersView}>
            {showDeletedUsers ? 'Hide Deleted Users' : 'Show Deleted Users'}
          </Button>
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>
            Print Users
          </Button>
          <Button 
            icon={<EditFilled />} 
            onClick={handleBulkEdit}
            disabled={selectedRowKeys.length === 0 || showTemporaryUsers}
            type="default"
          >
            Bulk Edit ({selectedRowKeys.length})
          </Button>
          {!showTemporaryUsers && (
            <Button type="primary" onClick={handleAddUser}>
              Add User
            </Button>
          )}
          <Button 
            type={showTemporaryUsers ? 'default' : 'primary'} 
            onClick={() => setShowTemporaryUsers(!showTemporaryUsers)}
          >
            {showTemporaryUsers ? 'Show Permanent Users' : 'Show Temporary Users'}
          </Button>
          {showTemporaryUsers && (
            <Button type="primary" onClick={handleAddTemporaryUser}>
              Create Temporary User
            </Button>
          )}
        </Space>
      </div>
      
      {/* Selection Controls */}
      {selectedRowKeys.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Text type="secondary">Selection Controls:</Text>
            <Button 
              size="small" 
              onClick={() => setSelectedRowKeys(filteredUsers.map(u => u.MobileNumber))}
            >
              Select All ({filteredUsers.length})
            </Button>
            <Button 
              size="small" 
              onClick={() => setSelectedRowKeys([])}
            >
              Select None
            </Button>
            <Button 
              size="small" 
              onClick={() => setSelectedRowKeys(filteredUsers.filter(u => u.UserType === 'WORKER').map(u => u.MobileNumber))}
            >
              Select Workers Only
            </Button>
            <Button 
              size="small" 
              onClick={() => setSelectedRowKeys(filteredUsers.filter(u => u.UserType === 'LEAD').map(u => u.MobileNumber))}
            >
              Select Leads Only
            </Button>
            <Button 
              size="small" 
              onClick={() => setSelectedRowKeys(filteredUsers.filter(u => u.UserType === 'SUPERVISOR').map(u => u.MobileNumber))}
            >
              Select Supervisors Only
            </Button>
          </Space>
        </div>
      )}
      
      {/* Selection Info */}
      {selectedRowKeys.length > 0 && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col span={8}>
              <Alert
                message={`${selectedRowKeys.length} user(s) selected`}
                type="info"
                showIcon
                style={{ margin: 0 }}
              />
            </Col>
            <Col span={12}>
              <Space size="large">
                <Text type="secondary">
                  Workers: <Tag color="blue">{getSelectedUsersInfo().summary.workers}</Tag>
                  Supervisors: <Tag color="green">{getSelectedUsersInfo().summary.supervisors}</Tag>
                </Text>
              </Space>
            </Col>
            <Col span={4} style={{ textAlign: 'right' }}>
              <Button size="small" onClick={() => setSelectedRowKeys([])}>
                Clear Selection
              </Button>
            </Col>
          </Row>
        </Card>
      )}
      
      {!showTemporaryUsers ? (
        <Table 
          columns={columns} 
          dataSource={filteredUsers} 
          rowKey="MobileNumber" 
          loading={loading} 
          scroll={{ x: 'max-content' }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            getCheckboxProps: (record) => ({
              disabled: record.activeStatus === 'inactive',
            }),
          }}
          style={{
            '& .ant-table-thead > tr > th': {
              backgroundColor: themeConfig.status.errorBg,
              color: 'var(--tertiary-text)',
              fontWeight: '600',
            },
            '& .ant-table-tbody > tr:nth-child(even) > td': {
              backgroundColor: themeConfig.background.tableRowEven,
            },
            '& .ant-table-tbody > tr:nth-child(odd) > td': {
              backgroundColor: themeConfig.background.tableRowOdd,
            },
            '& .ant-table-tbody > tr:hover > td': {
              backgroundColor: themeConfig.background.container,
            }
          }}
        />
      ) : (
        <Table 
          columns={[
            {
              title: 'Full Name',
              dataIndex: 'FullName',
              key: 'fullName',
              render: (text, record) => `${text}${record.Designation ? ` (${record.Designation})` : ''}`,
            },
            {
              title: 'Mobile Number',
              dataIndex: 'MobileNumber',
              key: 'mobileNumber',
            },
            {
              title: 'Rate',
              dataIndex: 'Rate',
              key: 'rate',
              render: (rate) => rate ? `₹${rate}` : 'N/A',
            },
            {
              title: 'Created At',
              dataIndex: 'CreatedAt',
              key: 'createdAt',
              render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : 'N/A',
            },
            {
              title: 'Action',
              key: 'action',
              render: (_, record) => (
                <Space size="middle">
                  <Button 
                    type="link" 
                    onClick={() => handleConvertToPermanent(record)}
                  >
                    Convert to Permanent
                  </Button>
                  <Popconfirm
                    title="Are you sure you want to delete this temporary user?"
                    onConfirm={() => handleDeleteTemporaryUser(record.MobileNumber)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="link" danger>
                      Delete
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]} 
          dataSource={temporaryUsers.filter(user => 
            user.FullName?.toLowerCase().includes(searchText.toLowerCase()) ||
            user.MobileNumber?.toLowerCase().includes(searchText.toLowerCase())
          )} 
          rowKey="MobileNumber" 
          loading={loading} 
          scroll={{ x: 'max-content' }}
        />
      )}
      
      {/* Deleted Users Table */}
      {showDeletedUsers && (
        <div style={{ marginTop: 24 }}>
          <h3>Deleted Users</h3>
          <Table 
            columns={[
              {
                title: 'Full Name',
                dataIndex: 'FullName',
                key: 'fullName',
                render: (text) => (
                  <span style={{ color: themeConfig.text.mutedLight }}>
                    {text}
                  </span>
                ),
              },
              {
                title: 'Job',
                dataIndex: 'Job',
                key: 'job',
                render: (text) => (
                  <span style={{ color: themeConfig.text.mutedLight }}>
                    {text || 'N/A'}
                  </span>
                ),
              },
              {
                title: 'Mobile Number',
                dataIndex: 'MobileNumber',
                key: 'mobileNumber',
                render: (text) => <span style={{ color: themeConfig.text.mutedLight }}>{text}</span>,
              },
              {
                title: 'User Type',
                dataIndex: 'UserType',
                key: 'userType',
                render: (text) => <span style={{ color: themeConfig.text.mutedLight }}>{text}</span>,
              },
              {
                title: 'Deleted At',
                dataIndex: 'DeletedAt',
                key: 'deletedAt',
                render: (date) => (
                  <span style={{ color: themeConfig.text.mutedLight }}>
                    {dayjs(date).format('YYYY-MM-DD HH:mm')}
                  </span>
                ),
              },
              {
                title: 'Deleted By',
                dataIndex: 'DeletedBy',
                key: 'deletedBy',
                render: (text) => <span style={{ color: themeConfig.text.mutedLight }}>{text}</span>,
              },
              {
                title: 'Action',
                key: 'action',
                render: (_, record) => (
                  <Button 
                    type="link" 
                    onClick={() => handleRestoreUser(record.MobileNumber)}
                    style={{ color: themeConfig.status.success }}
                  >
                    Restore
                  </Button>
                ),
              },
            ]} 
            dataSource={deletedUsers} 
            rowKey="MobileNumber" 
            loading={deletedUsersLoading} 
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 5 }}
            style={{
              '& .ant-table-thead > tr > th': {
                backgroundColor: themeConfig.status.errorBg,
                color: 'var(--tertiary-text)',
                fontWeight: '600',
              },
              '& .ant-table-tbody > tr:nth-child(even) > td': {
                backgroundColor: themeConfig.background.tableRowEven,
              },
              '& .ant-table-tbody > tr:nth-child(odd) > td': {
                backgroundColor: themeConfig.background.tableRowOdd,
              },
              '& .ant-table-tbody > tr:hover > td': {
                backgroundColor: themeConfig.background.container,
              }
            }}
          />
        </div>
      )}
      <Modal
        title={editingUser ? 'Edit User' : 'Add User'}
        visible={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        destroyOnClose
      >
        <UserForm form={form} initialValues={editingUser} jobOptions={jobOptions} />
      </Modal>

      {/* Print Modal */}
      <Modal
        title="Print Users Data"
        visible={isPrintModalVisible}
        onCancel={() => setIsPrintModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsPrintModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleDownloadCSV}>
            Download CSV
          </Button>,
          <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrintContent}>
            Print
          </Button>
        ]}
        width={1000}
        destroyOnClose
      >
        <div id="printable-content">
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <h2>Users Data Report</h2>
            <p>Generated on: {new Date().toLocaleDateString()}</p>
          </div>
          
          <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${themeConfig.border.light}` }}>
            <thead>
              <tr style={{ backgroundColor: themeConfig.background.tableHeader }}>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Full Name</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Mobile Number</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Aadhaar Card</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Rate</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Job</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Payment Account</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Employment Type</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>User Type</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Date of Joining</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Leave Status</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Active Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user, index) => (
                <tr key={user.MobileNumber} style={{ backgroundColor: index % 2 === 0 ? themeConfig.background.tableRowOdd : themeConfig.background.tableRowEven }}>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{user.FullName || 'N/A'}</td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{user.MobileNumber || 'N/A'}</td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{user.AadhaarCard || 'N/A'}</td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{user.Rate ? `₹${user.Rate}` : 'Not Set'}</td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{user.Job || 'N/A'}</td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{user.PaymentAccount || 'N/A'}</td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{user.EmploymentType || 'SHIFT'}</td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{user.UserType || 'N/A'}</td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{user.DateOfJoining || 'N/A'}</td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>
                    {(user.UserType === 'SUPERVISOR' || user.UserType === 'LEAD') ? (user.IsOnLeave ? 'On Leave' : 'Active') : 'N/A'}
                  </td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{user.activeStatus || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          
          <div style={{ marginTop: 20, textAlign: 'center' }}>
            <p>Total Users: {filteredUsers.length}</p>
          </div>
        </div>
      </Modal>

      {/* Bulk Edit Modal */}
      <Modal
        title={
          <Space>
            <EditFilled style={{ color: themeConfig.status.info }} />
            <span>Bulk Edit Users</span>
          </Space>
        }
        open={isBulkEditModalVisible}
        onCancel={handleBulkEditCancel}
        onOk={handleBulkEditOk}
        confirmLoading={bulkEditLoading}
        width={700}
        destroyOnClose
      >
        <Alert
          message={`Bulk Edit ${selectedRowKeys.length} Users`}
          description={`You are about to update ${selectedRowKeys.length} selected users. Only the fields you select below will be updated for all users.`}
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form form={bulkEditForm} layout="vertical">
          <Form.Item
            label="Select Fields to Update"
            required
            help="Choose which fields you want to update for all selected users"
          >
            <Checkbox.Group
              options={[
                { label: 'Rate', value: 'rate' },
                { label: 'Job', value: 'job' },
                { label: 'Employment Type', value: 'employmentType' },
                { label: 'User Type', value: 'userType' },
                { label: 'Date of Joining', value: 'dateOfJoining' },
                { label: 'Payment Account', value: 'paymentAccount' },
                { label: 'Aadhaar Card', value: 'aadhaarCard' },
              ]}
              onChange={handleFieldSelectionChange}
            />
          </Form.Item>

          <Divider />

          {/* Dynamic form fields based on selection */}
          {bulkEditFields.includes('rate') && (
            <Form.Item
              name="rate"
              label="Rate"
              rules={[{ type: 'number', min: 0, message: 'Rate must be a positive number' }]}
            >
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter new rate for all selected users"
                addonBefore="₹"
              />
            </Form.Item>
          )}

          {bulkEditFields.includes('job') && (
            <Form.Item
              name="job"
              label="Job"
              rules={[{ required: true, message: 'Please input the job!' }]}
            >
              <Input placeholder="Enter new job for all selected users" />
            </Form.Item>
          )}

          {bulkEditFields.includes('employmentType') && (
            <Form.Item
              name="employmentType"
              label="Employment Type"
              rules={[{ required: true, message: 'Please select the employment type!' }]}
            >
              <Select placeholder="Select new employment type for all selected users">
                <Option value="FIXED">Fixed</Option>
                <Option value="SHIFT">Shift</Option>
              </Select>
            </Form.Item>
          )}

          {bulkEditFields.includes('userType') && (
            <Form.Item
              name="userType"
              label="User Type"
              rules={[{ required: true, message: 'Please select the user type!' }]}
            >
              <Select placeholder="Select new user type for all selected users">
                <Option value="WORKER">Worker</Option>
                <Option value="LEAD">Lead</Option>
                <Option value="SUPERVISOR">Supervisor</Option>
              </Select>
            </Form.Item>
          )}

          {bulkEditFields.includes('dateOfJoining') && (
            <Form.Item
              name="dateOfJoining"
              label="Date of Joining"
            >
              <DatePicker 
                style={{ width: '100%' }} 
                format="YYYY-MM-DD" 
                showToday={true}
                disabledDate={(current) => current && current > dayjs().endOf('day')}
                placeholder="Select new date of joining for all selected users"
              />
            </Form.Item>
          )}

          {bulkEditFields.includes('paymentAccount') && (
            <Form.Item
              name="paymentAccount"
              label="Payment Account"
            >
              <AutoComplete
                options={paymentAccountOptions}
                placeholder="Enter new payment account details for all selected users"
                allowClear
                showSearch
                filterOption={(inputValue, option) =>
                  option?.value?.toLowerCase().includes(inputValue.toLowerCase())
                }
                notFoundContent="No suggestions found"
              />
            </Form.Item>
          )}

          {bulkEditFields.includes('aadhaarCard') && (
            <Form.Item
              name="aadhaarCard"
              label="Aadhaar Card"
            >
              <Input placeholder="Enter new Aadhaar card number for all selected users" />
            </Form.Item>
          )}

          {bulkEditFields.length === 0 && (
            <Alert
              message="No Fields Selected"
              description="Please select at least one field above to enable bulk editing."
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          {bulkEditFields.length > 0 && (
            <>
              <Divider />
              <Alert
                message="Update Preview"
                description={
                  <div>
                    <Text strong>Fields that will be updated:</Text>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                      {bulkEditFields.map(field => {
                        const fieldLabels = {
                          rate: 'Rate',
                          job: 'Job',
                          employmentType: 'Employment Type',
                          userType: 'User Type',
                          dateOfJoining: 'Date of Joining',
                          paymentAccount: 'Payment Account',
                          aadhaarCard: 'Aadhaar Card'
                        };
                        return <li key={field}>{fieldLabels[field]}</li>;
                      })}
                    </ul>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Only selected fields with values will be updated. Empty fields will be ignored.
                    </Text>
                  </div>
                }
                type="info"
                showIcon
                style={{ marginTop: 16 }}
              />
            </>
          )}
        </Form>
      </Modal>

      {/* Temporary User Creation Modal */}
      <Modal
        title="Create Temporary User (Naka Labour)"
        open={isTemporaryModalVisible}
        onOk={handleTemporaryModalOk}
        onCancel={() => {
          setIsTemporaryModalVisible(false);
          tempUserForm.resetFields();
        }}
        destroyOnClose
      >
        <Form form={tempUserForm} layout="vertical">
          <Form.Item
            name="mobileNumber"
            label="Mobile Number"
            rules={[
              { required: true, message: 'Please enter mobile number' },
              { pattern: /^\d{10}$/, message: 'Mobile number must be 10 digits' },
            ]}
          >
            <Input placeholder="Enter 10-digit mobile number" maxLength={10} />
          </Form.Item>
          <Form.Item
            name="fullName"
            label="Full Name"
            rules={[{ required: true, message: 'Please enter full name' }]}
          >
            <Input placeholder="Enter full name" />
          </Form.Item>
          <Form.Item
            name="rate"
            label="Rate (per shift)"
            rules={[
              { required: true, message: 'Please enter rate' },
              { type: 'number', min: 0.01, message: 'Rate must be greater than 0' },
            ]}
          >
            <InputNumber
              placeholder="Enter rate per shift"
              style={{ width: '100%' }}
              min={0}
              step={0.01}
              prefix="₹"
            />
          </Form.Item>
          <Form.Item
            name="designation"
            label="Designation"
          >
            <Input placeholder="e.g., Helper, Mason, etc." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Convert to Permanent Modal */}
      <Modal
        title="Convert Temporary User to Permanent"
        open={isConvertModalVisible}
        onOk={handleConvertModalOk}
        onCancel={() => {
          setIsConvertModalVisible(false);
          convertForm.resetFields();
          setTempUserToConvert(null);
        }}
        width={700}
        destroyOnClose
      >
        <Alert
          message="Convert Temporary User to Permanent"
          description="This will create a permanent user account. The temporary user records will be preserved but hidden."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
        <UserForm form={convertForm} jobOptions={jobOptions} />
      </Modal>

    </div>
  );
};

export default UserManagement; 